import {useLazyQuery} from "@apollo/client";
import {useFormContext} from "react-hook-form";

import {Button} from "@/components/button/Button.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {getFormError} from "@/lib/form/form.utils.ts";
import {GQL_WHOIS_LOOKUP, WhoIsLookUpResponse} from "@/lib/queries/checks.ts";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";

interface WhoIsAutoloadFieldProps extends FieldProps {}

export const WhoIsAutoloadField = ({
  name,
  tooltip,
  label,
  className,
  helpText,
  supportLink,
  required,
}: WhoIsAutoloadFieldProps) => {
  const [whoisLookup, {loading}] = useLazyQuery<WhoIsLookUpResponse>(GQL_WHOIS_LOOKUP);
  const {
    register,
    setValue,
    watch,
    trigger,
    setError,
    clearErrors,
    formState: {errors},
  } = useFormContext();

  const domain = watch(ServiceField.ADDRESS);
  const handleAutoload = async () => {
    await trigger(ServiceField.ADDRESS);
    if (!domain) {
      return;
    }

    const {data, error} = await whoisLookup({
      variables: {
        domain: domain,
      },
    });
    if (error) {
      console.error(error);
    }
    if (data?.whoisLookup?.errors) {
      Object.keys(data?.whoisLookup?.errors).forEach((key) => {
        setError(key, {message: data?.whoisLookup?.errors[key][0]});
      });
      return;
    }
    if (data?.whoisLookup?.expectString) {
      setValue(name, data?.whoisLookup?.expectString, {shouldDirty: true});
      clearErrors(name);
    }
  };
  const fieldError = getFormError(name, errors);
  return (
    <>
      <div className={className}>
        <FormLabel helpText={tooltip} forId={name} required={required}>
          {label}
        </FormLabel>
        <textarea
          id={name}
          className="form-control"
          {...register(name, {required: required})}
          disabled={true}
          rows={5}
        />
        <FormSubtext subText={helpText} supportLink={supportLink} error={fieldError?.toString()} />
      </div>
      <Button className="mt-2" onClick={handleAutoload} disabled={loading}>
        Refresh
      </Button>
    </>
  );
};
